import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../lib/firebase';
import {
  collection, getDocs, doc, updateDoc, orderBy, query
} from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';

const BRAND = '#214140';
const GREEN = '#1F8051';
const TEAL  = '#8BBAB8';
const LIGHT = '#DFEAF0';

const LEVEL_LABELS = { pending: 'Pending', basic: 'Basic', full: 'Full Partner', admin: 'Admin' };
const LEVEL_COLORS = { pending: '#f59e0b', basic: '#1F8051', full: '#214140', admin: '#7c3aed' };
const LEVEL_BG     = { pending: '#fffbeb', basic: '#f0fdf4', full: '#DFEAF0', admin: '#f5f3ff' };

const Badge = ({ level }) => (
  <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold"
    style={{ background: LEVEL_BG[level] || '#f3f4f6', color: LEVEL_COLORS[level] || '#6b7280', border: '1px solid ' + (LEVEL_COLORS[level] || '#e5e7eb') + '40' }}>
    {LEVEL_LABELS[level] || level}
  </span>
);

export default function DistributorAdminPage() {
  const navigate = useNavigate();
  const { user, profile, loading, signOut } = useAuth();
  const [users, setUsers]           = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [selected, setSelected]     = useState(null);
  const [notes, setNotes]           = useState('');
  const [saving, setSaving]         = useState(false);
  const [filter, setFilter]         = useState('all');
  const [search, setSearch]         = useState('');
  const [toast, setToast]           = useState('');

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const fetchUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'user_profiles'), orderBy('created_at', 'desc')));
      setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      showToast('Error loading users: ' + err.message);
    }
    setUsersLoading(false);
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user || profile?.access_level !== 'admin') {
      navigate('/distributors/login', { replace: true });
    } else {
      fetchUsers();
    }
  }, [user, profile, loading, navigate, fetchUsers]);

  const updateLevel = async (userId, newLevel) => {
    setSaving(true);
    const update = { access_level: newLevel };
    if (newLevel !== 'pending') update.approved_at = new Date().toISOString();
    try {
      await updateDoc(doc(db, 'user_profiles', userId), update);
      setUsers(u => u.map(x => x.id === userId ? { ...x, ...update } : x));
      if (selected?.id === userId) setSelected(s => ({ ...s, ...update }));
      showToast('Access updated to ' + LEVEL_LABELS[newLevel]);
    } catch (err) {
      showToast('Error: ' + err.message);
    }
    setSaving(false);
  };

  const saveNotes = async (userId) => {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'user_profiles', userId), { admin_notes: notes });
      setUsers(u => u.map(x => x.id === userId ? { ...x, admin_notes: notes } : x));
      showToast('Notes saved');
    } catch (err) {
      showToast('Error: ' + err.message);
    }
    setSaving(false);
  };

  if (loading || usersLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: BRAND }}>
        <p className="text-white opacity-75">Loading...</p>
      </div>
    );
  }

  const counts = { all: users.length, pending: 0, basic: 0, full: 0 };
  users.forEach(u => { if (counts[u.access_level] !== undefined) counts[u.access_level]++; });

  const filtered = users.filter(u => {
    const matchFilter = filter === 'all' || u.access_level === filter;
    const q = search.toLowerCase();
    const matchSearch = !q ||
      u.full_name?.toLowerCase().includes(q) ||
      u.company?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q);
    return matchFilter && matchSearch;
  });

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#f9fafb' }}>

      {toast && (
        <div className="fixed top-4 right-4 z-50 px-4 py-2.5 rounded-xl text-sm font-semibold text-white shadow-lg"
          style={{ background: GREEN }}>
          {toast}
        </div>
      )}

      {/* Header */}
      <header style={{ background: BRAND }} className="px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-white font-bold text-lg">Cordflex Admin</span>
          <span className="text-xs px-2.5 py-0.5 rounded-full font-semibold" style={{ background: '#7c3aed30', color: '#a78bfa' }}>Admin</span>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/distributors/portal')}
            className="text-sm px-3 py-1.5 rounded-lg transition-all hover:bg-white/10"
            style={{ color: TEAL, border: '1px solid ' + TEAL + '40' }}>
            View Portal
          </button>
          <button onClick={async () => { await signOut(); navigate('/distributors/login'); }}
            className="text-sm px-3 py-1.5 rounded-lg transition-all hover:bg-white/10"
            style={{ color: TEAL, border: '1px solid ' + TEAL + '40' }}>
            Sign Out
          </button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">

        {/* ── USER LIST ── */}
        <div className="w-full lg:w-96 flex flex-col bg-white" style={{ borderRight: '1px solid #e5e7eb' }}>

          {/* Stats */}
          <div className="px-4 py-3 grid grid-cols-4 gap-2" style={{ borderBottom: '1px solid #e5e7eb', background: LIGHT }}>
            {[['all','All',counts.all],['pending','Pending',counts.pending],['basic','Basic',counts.basic],['full','Full',counts.full]].map(([k,l,c]) => (
              <button key={k} onClick={() => setFilter(k)}
                className="text-center py-2 rounded-lg text-xs font-semibold transition-all"
                style={{ background: filter === k ? BRAND : 'white', color: filter === k ? 'white' : BRAND, border: '1px solid ' + (filter === k ? BRAND : '#e5e7eb') }}>
                <div className="text-base font-bold">{c}</div>
                <div>{l}</div>
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="px-4 py-3" style={{ borderBottom: '1px solid #e5e7eb' }}>
            <input className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2"
              placeholder="Search name, company, email..."
              value={search} onChange={e => setSearch(e.target.value)} />
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto">
            {filtered.length === 0 && <div className="text-center py-12 text-sm text-gray-400">No results</div>}
            {filtered.map(u => (
              <button key={u.id} onClick={() => { setSelected(u); setNotes(u.admin_notes || ''); }}
                className="w-full text-left px-4 py-3 transition-all hover:bg-gray-50"
                style={{ borderBottom: '1px solid #f3f4f6', background: selected?.id === u.id ? LIGHT : 'white' }}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate" style={{ color: BRAND }}>{u.full_name}</p>
                    <p className="text-xs text-gray-400 truncate">{u.company}</p>
                    <p className="text-xs text-gray-400 truncate">{u.territory}</p>
                  </div>
                  <div className="flex-shrink-0 mt-0.5"><Badge level={u.access_level} /></div>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  {u.created_at?.toDate ? u.created_at.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Date unknown'}
                </p>
              </button>
            ))}
          </div>
        </div>

        {/* ── DETAIL PANEL ── */}
        <div className="hidden lg:flex flex-1 flex-col overflow-y-auto p-6">
          {!selected ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center text-gray-400">
              <div className="text-4xl mb-3">👆</div>
              <p className="text-sm">Select a distributor from the list to manage their access</p>
            </div>
          ) : (
            <div className="max-w-xl space-y-6">

              {/* Profile header */}
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-2xl font-bold" style={{ color: BRAND }}>{selected.full_name}</h2>
                  <p className="text-gray-500">{selected.company}{selected.territory && ' · ' + selected.territory}</p>
                  <p className="text-sm text-gray-400">{selected.role_title}</p>
                </div>
                <Badge level={selected.access_level} />
              </div>

              {/* Contact */}
              <div className="bg-white rounded-xl p-5 space-y-2" style={{ border: '1px solid #e5e7eb' }}>
                <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: GREEN }}>Contact</p>
                <p className="text-sm"><span className="text-gray-400 w-16 inline-block">Email</span>
                  <a href={'mailto:' + (selected.email || '')} className="hover:underline" style={{ color: BRAND }}>{selected.email || '—'}</a></p>
                <p className="text-sm"><span className="text-gray-400 w-16 inline-block">Phone</span>{selected.phone || '—'}</p>
                <p className="text-sm"><span className="text-gray-400 w-16 inline-block">Applied</span>
                  {selected.created_at?.toDate ? selected.created_at.toDate().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '—'}</p>
                {selected.approved_at && (
                  <p className="text-sm"><span className="text-gray-400 w-16 inline-block">Approved</span>
                    {new Date(selected.approved_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                )}
              </div>

              {/* Application message */}
              {selected.message && (
                <div className="bg-white rounded-xl p-5" style={{ border: '1px solid #e5e7eb' }}>
                  <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: GREEN }}>Their Message</p>
                  <p className="text-sm text-gray-700 italic">"{selected.message}"</p>
                </div>
              )}

              {/* Access control */}
              <div className="bg-white rounded-xl p-5" style={{ border: '1px solid #e5e7eb' }}>
                <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: GREEN }}>Access Level</p>
                <div className="flex flex-wrap gap-2">
                  {[['pending','Pending Review'],['basic','Basic Access'],['full','Full Partner']].map(([lvl, label]) => (
                    <button key={lvl} onClick={() => updateLevel(selected.id, lvl)}
                      disabled={saving || selected.access_level === lvl}
                      className="px-4 py-2 rounded-lg text-sm font-semibold transition-all disabled:opacity-60"
                      style={{
                        background: selected.access_level === lvl ? LEVEL_COLORS[lvl] : 'white',
                        color: selected.access_level === lvl ? 'white' : LEVEL_COLORS[lvl],
                        border: '2px solid ' + LEVEL_COLORS[lvl]
                      }}>
                      {selected.access_level === lvl ? '✓ ' : ''}{label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-3">Takes effect on their next sign-in.</p>
              </div>

              {/* Admin notes */}
              <div className="bg-white rounded-xl p-5" style={{ border: '1px solid #e5e7eb' }}>
                <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: GREEN }}>Admin Notes</p>
                <textarea rows={4}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 mb-3"
                  placeholder="Notes about this distributor, territory, conversations..."
                  value={notes} onChange={e => setNotes(e.target.value)} />
                <button onClick={() => saveNotes(selected.id)} disabled={saving}
                  className="px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-60 transition-all hover:opacity-90"
                  style={{ background: BRAND }}>
                  {saving ? 'Saving...' : 'Save Notes'}
                </button>
              </div>

              {/* Quick actions */}
              <div className="bg-white rounded-xl p-5" style={{ border: '1px solid #e5e7eb' }}>
                <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: GREEN }}>Quick Actions</p>
                <div className="flex flex-wrap gap-2">
                  <a href={'mailto:' + (selected.email || '')}
                    className="px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all hover:opacity-90"
                    style={{ background: BRAND }}>
                    Email {selected.full_name?.split(' ')[0]}
                  </a>
                  <a href="https://calendly.com/davidusevitch/meet-with-david-nursetech-medical"
                    target="_blank" rel="noreferrer"
                    className="px-4 py-2 rounded-lg text-sm font-semibold transition-all hover:opacity-90"
                    style={{ background: LIGHT, color: BRAND, border: '1px solid ' + TEAL }}>
                    Schedule Call
                  </a>
                </div>
              </div>

            </div>
          )}
        </div>
      </div>
    </div>
  );
}
